import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, MenuItem } from '@mui/material';
import { IconChevronDown } from '@tabler/icons-react';

import { Button, Typography } from '@zendesk/forethought-elements';
import { useIsFeatureFlagEnabled } from 'src/hooks/hooks';
import { useCreateInstructionMutation } from 'src/services/ai-instruction/aiInstructionApi';
import { useCreateContentSnippetMutation } from 'src/services/content-snippets/contentSnippetsApi';
import { setGlobalToastOptions } from 'src/slices/ui/uiSlice';
import { useAppDispatch } from 'src/store/hooks';
import { Routes } from 'src/utils/enums';

export const AddKnowledgeMenuButton = ({
  onSuccess,
}: {
  onSuccess: (newId: string) => void;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const dispatch = useAppDispatch();
  const isContentSnippetsEnabled = useIsFeatureFlagEnabled('content_snippets');
  const [createInstruction, { isLoading: isLoadingCreateInstruction }] =
    useCreateInstructionMutation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const onCreateNewInstruction = useCallback(async () => {
    const res = await createInstruction({
      brands: [],
      instructions: '',
      is_active: true,
      name: '',
    }).unwrap();
    onSuccess(res.ai_instruction_id);
  }, [createInstruction, onSuccess]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleKnowledgeCoachingClick = () => {
    onCreateNewInstruction();
    handleClose();
  };

  const [createContentSnippet, { isLoading: isLoadingCreateContentSnippet }] =
    useCreateContentSnippetMutation();

  const isLoading = isLoadingCreateInstruction || isLoadingCreateContentSnippet;

  const handleContentSnippetClick = async () => {
    try {
      const res = await createContentSnippet({
        brands: [],
        content: '',
        is_active: true,
        name: '',
        products: [],
      }).unwrap();

      handleClose();

      window.open(
        `${Routes.KNOWLEDGE}?tab=content+snippets&contentSnippet=${res.content_snippet_id}`,
        '_blank',
        'noreferrer',
      );
    } catch (error) {
      dispatch(
        setGlobalToastOptions({
          autoHideDuration: 3000,
          title: t('addKnowledgeMenuButton.failedToCreateContentSnippet'),
          variant: 'danger',
        }),
      );
    }
  };

  return (
    <>
      <Button
        aria-label={t('addKnowledgeMenuButton.addAriaLabel')}
        disabled={isLoading}
        isLoading={isLoading}
        onClick={handleClick}
        variant='secondary'
      >
        {t('addKnowledgeMenuButton.addButton')}
        <IconChevronDown size={16} />
      </Button>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        onClose={handleClose}
        open={open}
        slotProps={{
          paper: {
            sx: theme => ({
              background: '#FFF',
              border: '1px solid ' + theme.palette.colors.grey[300],
              borderRadius: '4px',
              boxShadow:
                '0px 0px 1px 0px rgba(0, 12, 32, 0.04), 2px 5px 8px 0px rgba(3, 17, 38, 0.11)',
            }),
          },
        }}
        transformOrigin={{
          horizontal: 'left',
          vertical: 'top',
        }}
      >
        <MenuItem onClick={handleKnowledgeCoachingClick}>
          <Typography variant='font14'>
            {t('addKnowledgeMenuButton.knowledgeCoaching')}
          </Typography>
        </MenuItem>
        {isContentSnippetsEnabled && (
          <MenuItem onClick={handleContentSnippetClick}>
            <Typography variant='font14'>
              {t('addKnowledgeMenuButton.contentSnippet')}
            </Typography>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};
